import { computed, ref } from 'vue'
import { api, unwrap } from '../api'
import type { Dataset, Document, Folder, Message, RecentUpload, SearchContext, UploadStats, User } from '../types'
import { childFolders, compareFolderNames } from '../utils/folderCounts'

export function useWorkspace() {
  const authenticated = ref(!!localStorage.getItem('kb-token'))
  const user = ref<User | null>(null)
  const captcha = ref({ image: '', idKey: '' })
  const loginForm = ref({ userName: '', password: '', validateCode: '' })
  const datasets = ref<Dataset[]>([])
  const documents = ref<Document[]>([])
  const uploadStats = ref<UploadStats[]>([{ label: '近三天', days: 3, count: 0 }, { label: '近一周', days: 7, count: 0 }, { label: '近三个月', days: 90, count: 0 }, { label: '近半年', days: 180, count: 0 }])
  const folders = ref<Folder[]>([])
  const foldersLoading = ref(false)
  const fileFolder = ref('root')
  const expandedFolders = ref(new Set<string>(['root']))
  const activeDataset = ref('')
  const loading = ref(false)
  const error = ref('')
  const messages = ref<Message[]>([])
  // A search-result click can replace the chat context while an earlier stream
  // is still flushing. Keep a generation so that stale stream callbacks never
  // write a sparse entry into the new message list.
  const messageGeneration = ref(0)
  const recentUploads = ref<RecentUpload[]>([])
  let activeStreamController: AbortController | null = null
  let activeStreamStop: (() => void) | null = null
  let documentLoadGeneration = 0
  let folderLoadGeneration = 0

  const totalDocs = computed(() => datasets.value.reduce((sum, item) => sum + Number(item.document_count || 0), 0))
  const activeDatasetName = computed(() => datasets.value.find(item => item.id === activeDataset.value)?.name || '未选择知识库')
  const visibleFolders = computed(() => childFolders(folders.value, fileFolder.value))
  const visibleDocuments = computed(() => documents.value.filter(doc => (doc.folder_id || 'root') === fileFolder.value))

  async function loadCaptcha() {
    const result = await api<any>('/api/auth/captcha')
    const payload = result?.data?.data ? result.data : result?.data || result || {}
    const image = payload.data || payload.image || payload.img || ''
    captcha.value = { image: String(image).startsWith('data:') ? String(image) : `data:image/png;base64,${image}`, idKey: payload.idkey || payload.idKey || '' }
  }

  async function login() {
    error.value = ''
    try {
      const result = await api<{ data?: { token?: string; user?: User } }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ ...loginForm.value, idKey: captcha.value.idKey }) })
      const payload = result.data || {}
      if (!payload.token) throw new Error('登录接口未返回会话令牌')
      localStorage.setItem('kb-token', payload.token); user.value = payload.user || null; authenticated.value = true
      await loadWorkspace()
    } catch (err) { error.value = err instanceof Error ? err.message : '登录失败'; await loadCaptcha().catch(() => {}) }
  }

  async function logout() {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => {})
    localStorage.removeItem('kb-token'); authenticated.value = false; user.value = null
    window.location.reload()
  }

  async function loadWorkspace(options: { skipDocuments?: boolean } = {}) {
    loading.value = true; error.value = ''
    try {
      const result = unwrap<Dataset[] | { datasets?: Dataset[] }>(await api('/api/ragflow/datasets'))
      datasets.value = Array.isArray(result) ? result : result.datasets || []
      if (!activeDataset.value && datasets.value[0]) activeDataset.value = datasets.value[0].id
      if (activeDataset.value && !options.skipDocuments) await loadDocuments()
      if (!options.skipDocuments) void loadUploadStats().catch(() => {})
    } catch (err) { error.value = err instanceof Error ? err.message : '工作区加载失败' }
    finally { loading.value = false }
  }

  async function loadUploadStats() {
    const allDocs = (await Promise.all(datasets.value.map(async dataset => { try { const docs = unwrap<Document[]>(await api(`/api/ragflow/documents?dataset_id=${encodeURIComponent(dataset.id)}`)) || []; return docs.map(doc => ({ ...doc, dataset_id: dataset.id, dataset_name: dataset.name || dataset.id })) } catch { return [] } }))).flat()
    recentUploads.value = allDocs.slice().sort((a, b) => { const toTime = (doc: RecentUpload) => { const raw = doc.source_file?.uploaded_at; return typeof raw === 'number' ? raw : Date.parse(String(raw || '')) / 1000 }; return toTime(b) - toTime(a) })
    const now = Date.now() / 1000
    uploadStats.value = uploadStats.value.map(item => ({ ...item, count: allDocs.filter(doc => { const raw = doc.source_file?.uploaded_at; const time = typeof raw === 'number' ? raw : Date.parse(String(raw || '')) / 1000; return Number.isFinite(time) && now - time <= item.days * 86400 }).length }))
  }

  async function loadDocuments() {
    const datasetId = activeDataset.value
    if (!datasetId) return
    const generation = ++documentLoadGeneration
    // Change the visible data immediately. This prevents the previous knowledge
    // base from remaining selectable while the new request is in flight.
    folderLoadGeneration += 1
    documents.value = []
    folders.value = []
    fileFolder.value = 'root'
    expandedFolders.value = new Set(['root'])
    foldersLoading.value = true
    try {
      const result = unwrap<Document[]>(await api(`/api/ragflow/documents?dataset_id=${encodeURIComponent(datasetId)}`))
      if (generation !== documentLoadGeneration || activeDataset.value !== datasetId) return
      documents.value = Array.isArray(result) ? result : []
      await loadFolders(datasetId)
    } catch (err) {
      if (generation === documentLoadGeneration && activeDataset.value === datasetId) { foldersLoading.value = false; throw err }
    }
  }

  async function loadFolders(datasetId = activeDataset.value) {
    if (!datasetId) return
    const generation = ++folderLoadGeneration
    foldersLoading.value = true
    try {
      const previousFolder = fileFolder.value
      const result = unwrap<{ folders?: Folder[] }>(await api(`/api/workspace/folders?dataset_id=${encodeURIComponent(datasetId)}`))
      if (generation !== folderLoadGeneration || activeDataset.value !== datasetId) return
      folders.value = [...(result.folders || [])].sort(compareFolderNames)
      fileFolder.value = previousFolder === 'root' || folders.value.some(folder => folder.id === previousFolder) ? previousFolder : 'root'
      expandedFolders.value = new Set(['root'])
    } finally { if (generation === folderLoadGeneration) foldersLoading.value = false }
  }
  function selectFolder(folderId: string) { fileFolder.value = folderId }
  function toggleFolder(folderId: string) { const next = new Set(expandedFolders.value); next.has(folderId) ? next.delete(folderId) : next.add(folderId); expandedFolders.value = next }

  async function sendChat(question: string) {
    if (!question.trim() || loading.value) return
    messages.value.push({ role: 'user', content: question.trim() }); loading.value = true
    try {
      const result = await api<{ data?: { answer?: string }; references?: Message['references'] }>('/api/ragflow/chat', { method: 'POST', body: JSON.stringify({ question: question.trim(), dataset_id: activeDataset.value, dataset_ids: activeDataset.value ? [activeDataset.value] : [], top_k: 5, messages: messages.value }) })
      const payload = unwrap<{ answer?: string }>(result)
      messages.value.push({ role: 'assistant', content: payload?.answer || '没有得到可展示的回答。', references: result.references || [] })
      await api('/api/chat/history', { method: 'POST', body: JSON.stringify({ dataset_ids: activeDataset.value ? [activeDataset.value] : [], messages: messages.value, turns: messages.value }) }).catch(() => {})
    } catch (err) { messages.value.push({ role: 'assistant', content: err instanceof Error ? err.message : '问答请求失败' }) }
    finally { loading.value = false }
  }

  async function streamChat(question: string, folderIds: string[] = [], context?: SearchContext | null, datasetIds: string[] = [], roleId?: string) {
    if (!question.trim() || loading.value) return
    const userQuestion = question.trim()
    messages.value.push({ role: 'user', content: userQuestion })
    const assistantIndex = messages.value.push({ role: 'assistant', content: '' }) - 1
    const generation = messageGeneration.value
    const controller = new AbortController()
    activeStreamController = controller
    let stoppedByUser = false
    let stopStreaming = () => { stoppedByUser = true; controller.abort() }
    activeStreamStop = stopStreaming
    loading.value = true
    try {
      const requestDatasetIds = datasetIds.length ? datasetIds : (activeDataset.value ? [activeDataset.value] : [])
      const chatHistory = messages.value.slice(0, assistantIndex).map(message => ({ role: message.role, content: message.content }))
      const response = await fetch('/api/ragflow/chat/stream', { method: 'POST', signal: controller.signal, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('kb-token') || ''}` }, body: JSON.stringify({ question: userQuestion, dataset_id: activeDataset.value, dataset_ids: requestDatasetIds, folder_dataset_id: folderIds.length ? activeDataset.value : null, folder_scopes: folderIds.length ? [{ dataset_id: activeDataset.value, folder_ids: folderIds }] : [], document_id: context?.document_id || null, chat_context: context ? { search_question: context.search_question, chunk_content: context.chunk_content } : {}, role_id: roleId || null, messages: chatHistory }) })
      if (!response.ok || !response.body) throw new Error('流式问答请求失败')
      const reader = response.body.getReader(); const decoder = new TextDecoder(); const STREAM_CHAR_INTERVAL = 45; let buffer = ''; let answer = ''; let pending = ''; let finalAnswer = ''; let references: Message['references'] = []; let pendingReferences: Message['references'] = []; let flushTimer: ReturnType<typeof setInterval> | null = null
      stopStreaming = () => { stoppedByUser = true; pending = ''; if (flushTimer) { clearInterval(flushTimer); flushTimer = null }; controller.abort() }
      activeStreamStop = stopStreaming
      const updateAssistant = () => {
        const current = messages.value[assistantIndex]
        if (generation !== messageGeneration.value || !current || current.role !== 'assistant') return false
        messages.value[assistantIndex] = { ...current, content: answer || '正在生成回答…', references }
        return true
      }
      const flushOne = () => { if (!pending) { if (flushTimer) { clearInterval(flushTimer); flushTimer = null }; return }; answer += pending[0]; pending = pending.slice(1); if (!updateAssistant()) { pending = ''; if (flushTimer) { clearInterval(flushTimer); flushTimer = null } } }
      const startFlushing = () => { if (!flushTimer) flushTimer = setInterval(flushOne, STREAM_CHAR_INTERVAL) }
      const drainPending = async () => { while (pending) { flushOne(); await new Promise(resolve => setTimeout(resolve, STREAM_CHAR_INTERVAL)) } }
      while (true) { const { value, done } = await reader.read(); buffer += decoder.decode(value || new Uint8Array(), { stream: !done }); const lines = buffer.split('\n'); buffer = lines.pop() || ''; for (const line of lines) { if (!line.trim()) continue; const event = JSON.parse(line); if (event.type === 'delta') { pending += event.content || ''; startFlushing() } if (event.type === 'final') { finalAnswer = event.answer || ''; pendingReferences = event.references || []; startFlushing() } if (event.type === 'error') throw new Error(event.message || '模型回答失败') } if (done) break }
      await drainPending()
      if (flushTimer) { clearInterval(flushTimer); flushTimer = null }
      if (finalAnswer && finalAnswer !== answer) answer = finalAnswer
      references = pendingReferences
      updateAssistant()
      if (!answer) updateAssistant()
      await api('/api/chat/history', { method: 'POST', body: JSON.stringify({ dataset_ids: [activeDataset.value], messages: messages.value, turns: messages.value }) }).catch(() => {})
    } catch (err) { if (!stoppedByUser) messages.value[assistantIndex] = { role: 'assistant', content: err instanceof Error ? err.message : '问答请求失败' } } finally { if (activeStreamController === controller) activeStreamController = null; loading.value = false }
  }

  function stopChat() { activeStreamStop?.() }

  function restoreMessages(next: Message[]) { messageGeneration.value += 1; messages.value = next }
  function clearChatMessages() { messageGeneration.value += 1; messages.value = [] }

  async function bootstrap(options: { skipDocuments?: boolean } = {}) {
    if (!authenticated.value) { await loadCaptcha().catch(() => {}); return }
    try { const payload = unwrap<{ user?: User }>(await api('/api/auth/me')); user.value = payload.user || payload as User; await loadWorkspace(options) }
    catch { authenticated.value = false; localStorage.removeItem('kb-token'); await loadCaptcha().catch(() => {}) }
  }

  return { authenticated, user, captcha, loginForm, datasets, documents, folders, foldersLoading, uploadStats, recentUploads, fileFolder, expandedFolders, visibleFolders, visibleDocuments, activeDataset, activeDatasetName, totalDocs, loading, error, messages, loadCaptcha, login, logout, loadWorkspace, loadDocuments, loadFolders, loadUploadStats, selectFolder, toggleFolder, sendChat, streamChat, stopChat, restoreMessages, clearChatMessages, bootstrap }
}
